/* eslint-disable @typescript-eslint/no-explicit-any */
import { getMatchEquationText, getTeamScores } from "../../helper/helper";
import { getPlayoffLabel } from "../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/matchDetailDisplay";
import type { MatchBucket } from "./useTournamentMatches";

const COLORS = ["#2B4C8C", "#3D6B4F", "#5B4A8F", "#A35A1F", "#7E3A55", "#2C6A80", "#8A6D24", "#4A5262", "#6B3D8C", "#8C2B2B"];
export const colorFor = (s?: string | null) => COLORS[[...(s ?? "")].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
export const codeFor = (name?: string | null, code?: string | null) => (code && code.trim() ? code.trim().slice(0, 3).toUpperCase() : (name ?? "?").split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 3).toUpperCase());

export type TeamVm = { id: string; name: string; code: string; color: string; image?: string; score?: string; overs?: string; batting?: boolean; won?: boolean; lost?: boolean; dnb?: boolean };
export type MatchVm = {
  id: string; bucket: MatchBucket; no: number; label: string; start: Date | null; dayKey: string; dayLabel: string; time: string; ground: string;
  a: TeamVm; b: TeamVm; resultTitle: string; resultSub: string; marginTitle: string; marginSub: string; outcome: "W" | "NR" | "T" | null; potm?: string; potmTeam?: string; potmLine?: string;
  hasLiveStream: boolean; raw: any; firstInnings: number; ms: number;
};

const dayKeyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const dayLabelOf = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
const timeOf = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

const teamOf = (t: any): TeamVm => ({ id: t?.teamId ?? t?.teamSnapshotId ?? t?.teamName ?? "", name: t?.teamName ?? "-", code: codeFor(t?.teamName, t?.teamCode), color: colorFor(t?.teamName), image: t?.imageUrl || undefined });

export const buildMatchVm = (raw: any, bucket: MatchBucket): MatchVm => {
  const s = getTeamScores(raw);
  const one = teamOf(raw.teamOne), two = teamOf(raw.teamTwo);
  // order so the side that batted first is on top, as the mobile app does
  let a = one, b = two;
  if (s.firstTeamName && norm(s.firstTeamName) === norm(two.name)) { a = two; b = one; }
  const scoreFor = (t: TeamVm) => {
    const first = norm(t.name) === norm(s.firstTeamName);
    const text = first ? s.firstTeamScoreText : s.secondTeamScoreText;
    const inn = first ? s.firstTeamScore : s.secondTeamScore;
    return { score: text ?? undefined, overs: inn ? `${inn.over}.${inn.ball ?? 0}${inn.maxOvers ? `/${inn.maxOvers}` : ""}` : undefined };
  };
  Object.assign(a, scoreFor(a)); Object.assign(b, scoreFor(b));
  a.dnb = !a.score; b.dnb = !b.score;

  const innings = raw.inningScorecardList ?? [];
  if (bucket === "live" && innings.length) {
    const last = innings[innings.length - 1];
    [a, b].forEach((t) => { t.batting = norm(t.name) === norm(last.battingTeamName); });
  }

  const eq = getMatchEquationText(raw);
  let outcome: MatchVm["outcome"] = null;
  let resultTitle = eq, marginTitle = eq;
  const marginSub = "";
  if (bucket === "past") {
    const lower = norm(eq);
    if (/no result|abandon|washed/.test(lower)) outcome = "NR";
    else if (/\btied?\b/.test(lower)) outcome = "T";
    else {
      outcome = "W";
      const m = eq.match(/^(.*?)\s+won\b/i);
      const winner = m ? norm(m[1]) : "";
      a.won = !!winner && norm(a.name) === winner; b.won = !!winner && norm(b.name) === winner;
      a.lost = b.won; b.lost = a.won;
      if (!winner) outcome = null;
    }
    const by = eq.match(/\bby\s+(.+)$/i);
    marginTitle = by ? `By ${by[1]}` : eq;
    resultTitle = eq;
  }
  const start = raw.startDate ? new Date(raw.startDate) : null;
  const valid = start && !Number.isNaN(start.getTime()) ? start : null;
  const playoff = getPlayoffLabel(raw.playoffType);
  const pm = raw.playerOfTheMatch;
  const potm = pm ? [pm.playerFirstName, pm.playerLastName].filter(Boolean).join(" ") || (typeof pm === "string" ? pm : "") : "";
  return {
    id: raw.matchId, bucket, no: 0, label: playoff ?? "", start: valid, dayKey: valid ? dayKeyOf(valid) : "", dayLabel: valid ? dayLabelOf(valid) : "Date to be announced",
    time: valid ? timeOf(valid) : "", ground: raw.groundId?.groundName ?? raw.ground?.groundName ?? raw.city ?? "", a, b, resultTitle, resultSub: "", marginTitle, marginSub, outcome,
    potm: potm || undefined, hasLiveStream: !!raw.hasLiveStream, raw, firstInnings: innings.length, ms: valid ? valid.getTime() : 0,
  };
};

/**
 * Number the league matches by match date and time ("Match 7"). Playoff matches (Final, Semi Final...) carry their
 * stage badge instead and do not use up a number; matches with no date go last.
 */
export const buildAllVms = (live: any[], up: any[], past: any[]) => {
  const all: MatchVm[] = [...past.map((m) => buildMatchVm(m, "past")), ...live.map((m) => buildMatchVm(m, "live")), ...up.map((m) => buildMatchVm(m, "up"))];
  all
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !m.label)
    .sort((x, y) => (x.m.start ? 0 : 1) - (y.m.start ? 0 : 1) || x.m.ms - y.m.ms || x.i - y.i)
    .forEach(({ m }, n) => { m.no = n + 1; });
  return all;
};

export const startsIn = (start: Date | null, now = Date.now()) => {
  if (!start) return { text: "Start time to be announced", when: "TBA" };
  const diff = Math.round((start.getTime() - now) / 60000);
  const days = Math.floor((new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime() - new Date(new Date(now).getFullYear(), new Date(now).getMonth(), new Date(now).getDate()).getTime()) / 86400000);
  const when = days === 0 ? "Today" : days === 1 ? "Tomorrow" : start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  if (diff <= 0) return { text: "Starting soon", when };
  if (diff < 60) return { text: `Starts in ${diff}m`, when };
  if (diff < 1440) return { text: `Starts in ${Math.floor(diff / 60)}h ${diff % 60}m`, when };
  return { text: `Starts in ${days} day${days === 1 ? "" : "s"}`, when };
};
